import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import eventsApi from 'api/requests/events';
import Checkin from '../Checkin';
import ActiveEventCheckin from './ActiveEventCheckin';
import findSessionInfo from './eventSession';
import Loader from 'components/_UI/Loader';
import { FullyCentered } from 'styled/alignment/Center';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

export default function CheckinHome() {
    const [loading, setLoading] = useState(true);
    const [activeEvents, setActiveEvents] = useState([]);
    const [selectedSlug, setSelectedSlug] = useState(null);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);

            try {
                const { data: response } = await eventsApi.myEvents();
                if (cancelled) return;

                // GET /events/my-events embrulha os buckets num nível extra de
                // `data` (response.data.data.now), fora do padrão de outros
                // endpoints — confirmado em MyEventsController::__invoke.
                // "Ativo" cobre hoje + próximos — não só o dia de hoje.
                const now = response?.data?.now?.items?.data || [];
                const next = response?.data?.next?.items?.data || [];
                const items = [...now, ...next];
                setActiveEvents(items);
                setSelectedSlug(items[0]?.slug || null);
            } catch (error) {
                if (!cancelled) setActiveEvents([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <FullyCentered height='240px'>
                <Loader />
            </FullyCentered>
        );
    }

    // Sem evento ativo hoje (ou erro na busca) — formulário genérico de sempre,
    // check-in por código/QR nunca pode ficar bloqueado por essa camada nova.
    if (activeEvents.length === 0) {
        return <Checkin />;
    }

    const selectedEvent = activeEvents.find((ev) => ev.slug === selectedSlug) || activeEvents[0];

    return (
        <Container>
            {activeEvents.length > 1 && (
                <>
                    <SwitcherLabel>Seus eventos</SwitcherLabel>
                    <SwitcherRow role='tablist' aria-label='Seus eventos'>
                        {activeEvents.map((ev) => (
                            <SwitcherPill
                                key={ev.slug}
                                type='button'
                                role='tab'
                                aria-selected={ev.slug === selectedEvent.slug}
                                $active={ev.slug === selectedEvent.slug}
                                onClick={() => setSelectedSlug(ev.slug)}
                            >
                                {ev.name}
                                {findSessionInfo(ev.dates)?.isToday && <TodayBadge>Hoje</TodayBadge>}
                            </SwitcherPill>
                        ))}
                    </SwitcherRow>
                </>
            )}

            {/* key força remount completo (com EventProvider novo) a cada troca,
                pra não vazar filtro/estado do evento anterior. */}
            <ActiveEventCheckin key={selectedEvent.slug} event={selectedEvent} />
        </Container>
    );
}

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SwitcherLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const SwitcherRow = styled.div`
    display: inline-flex;
    align-self: flex-start;
    max-width: 100%;
    padding: 3px;
    border-radius: 10px;
    background: ${colors.neutralBackground};
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const SwitcherPill = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
    white-space: nowrap;
    cursor: pointer;
    background: ${({ $active }) => ($active ? colors.neutralLight : 'transparent')};
    color: ${({ $active }) => ($active ? colors.nightDark : colors.night)};
    box-shadow: ${({ $active }) => ($active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none')};
    transition: background 0.15s ease, color 0.15s ease;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const TodayBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 999px;
    background: ${panelAccent(colors.secondaryPurple)}18;
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;
